<template>
  <span>
    <CollapsableSection
      v-if="checklistHeader"
      :class="[
        $style.checklistContainer, {
          [$style.checklistDisabled]: !expandable,
          [$style.extraPadding]: checklist.newFeature,
        }
      ]"
      :collapse="collapsed"
      :showCaret="false"
      :showSecondaryTitleContent="false"
      title=""
      @toggleCollapse="collapsed => toggleCollapse(null, collapsed)"
    >
      <template v-slot:title>
        <div
          :class="$style.feature"
          :data-test-id="$testId(createDataTestId(checklist.feature))"
        >
          <div>
            <div
              v-if="checklist.newFeature"
              :class="[
                $style.newFeature,
                showRemoveButton ? $style.newFeatureSpacingSmall : $style.newFeatureSpacing
              ]"
            />
            <Icon
              v-else
              :class="checklistComplete ? $style.checklistComplete : $style.checklistIncomplete"
              icon="checkmarkCircle"
              inline
              :scale="1.25"
            />
          </div>
          <span :class="$style.featureTitle">
            <HeadingSmall
              v-if="showResourceItem"
              :class="{
                [$style.headerText]: true,
                [$style.complete]: checklistComplete
              }"
            >
              {{ checklistHeader }}
            </HeadingSmall>
            <HeadingExtraSmall
              v-else
              :class="{
                [$style.headerText]: true,
                [$style.complete]: checklistComplete
              }"
            >
              {{ checklistHeader }}
            </HeadingExtraSmall>
          </span>
          <CollapseCaret
            v-if="expandable"
            :class="$style.caret"
            :collapsed="collapsed"
          />
        </div>
      </template>

      <Card
        v-if="showPasswordManagementCard"
        :class="$style.card"
        type="warning"
      >
        <template v-slot:body>
          <div
            :class="$style.cardContent"
          >
            <Icon
              :class="$style.infoIcon"
              icon="info"
              :scale="1.25"
            />
            <ParagraphBody>
              <strong>Required:</strong>
              Enrollment in Password Manager requires a JumpCloud user account, even for admins.
            </ParagraphBody>
          </div>
        </template>
      </Card>

      <div
        v-if="checklist.subText && showSubtext"
        :class="$style.subText"
      >
        <ParagraphBody>
          {{ checklist.subText }}
        </ParagraphBody>
      </div>

      <div :class="$style.checklistSectionContent">
        <div
          v-if="expandable"
          :class="$style.taskContainer"
        >
          <ImplementationChecklistTask
            v-for="(task, index) in checklist.tasks"
            :key="task.headerText"
            :checklist="checklist"
            :task="task"
            :taskIndex="index"
            @event="triggerSegmentEvent"
          />
          <div v-if="checklist.autoPopulated && showRemoveButton">
            <hr :class="$style.divider">
            <ButtonSecondary
              :class="$style.removeBtn"
              :data-test-id="$testId('removeButton')"
              text="Remove"
              @click="removeFeature"
            />
          </div>
        </div>
        <ChecklistResource
          v-if="showResourceItem"
          :data-test-id="$testId(createDataTestId(checklist.feature))"
          :feature="checklist.feature"
        />
      </div>
    </CollapsableSection>
  </span>
</template>

<script>
import {
  ButtonSecondary,
  Card,
  HeadingExtraSmall,
  HeadingSmall,
  Icon,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { implementationChecklistRegion } from 'client/components/ImplementationChecklist/ImplementationChecklistConstants';
import { implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import { mapActions, mapGetters } from 'vuex';
import { merge } from 'lodash';
import {
  onboardingFeatures,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import ChecklistResource from 'client/components/ConsolidatedChecklist/components/ChecklistResource.vue';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import CollapseCaret from 'client/components/ContentSection/CollapseCaret.vue';
import Util from 'client/util/Util';

import ImplementationChecklistTask from './ImplementationChecklistTask.vue';

const onboardingModule = 'OnboardingModule';

export default {
  name: 'ImplementationChecklistItem',

  components: {
    ButtonSecondary,
    Card,
    ChecklistResource,
    CollapsableSection,
    CollapseCaret,
    HeadingExtraSmall,
    HeadingSmall,
    Icon,
    ImplementationChecklistTask,
    ParagraphBody,
  },

  Util,
  onboardingFeatures,

  props: {
    checklist: {
      required: true,
      type: Object,
      validator: function validator(value) {
        if (!value) {
          return false;
        }

        if (!value.tasks || !value.feature) {
          return false;
        }

        return true;
      },
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    showChecklistResource: {
      required: true,
      type: Boolean,
    },
    showRemoveButton: {
      type: Boolean,
      default: false,
    },
    startCollapsed: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      collapsed: this.startCollapsed || false,
    };
  },

  computed: {
    ...mapGetters(onboardingModule, {
      directOnboardingFeature: 'getDirectOnboardingFeature',
    }),

    checklistComplete() {
      return this.checklist.tasks.every(task => task.complete === true);
    },

    checklistHeader() {
      return implementationChecklists[this.checklist.feature]
        ? implementationChecklists[this.checklist.feature].headerText : this.checklist.headerText;
    },

    expandable() {
      return this.checklist.tasks.length > 1;
    },

    showPasswordManagementCard() {
      return this.checklist.feature === onboardingFeatures.passwordManagement
        && this.showChecklistResource;
    },

    showResourceItem() {
      return this.showChecklistResource && !this.$options.Util.isSmallScreen();
    },

    showSubtext() {
      return this.checklist.feature === this.directOnboardingFeature;
    },
  },

  watch: {
    startCollapsed(newValue) {
      if (newValue) {
        this.collapsed = newValue;
      }
    },
  },

  created() {
    const selectedFeature = this.checklist.feature;
    const defaultChecklistValues = implementationChecklists[selectedFeature];
    merge(this.checklist, defaultChecklistValues);
  },

  methods: {
    ...mapActions(onboardingModule, [
      'clearNewFeatureAlert',
      'removeFeatureFromImplementationChecklist',
    ]),

    createDataTestId(humanReadableString) {
      return Util.createDataTestId(humanReadableString);
    },

    removeFeature() {
      UiEvents.triggerButtonClicked({
        description: `Removes the automatically populated ${this.checklist.headerText} feature from the Implementation Checklist`,
        page: this.$page,
        region: implementationChecklistRegion,
        section: `${this.checklist.headerText} Feature`,
        text: 'Remove',
      });
      this.removeFeatureFromImplementationChecklist(this.checklist);
    },

    toggleCollapse(ev, collapsedEventValue) {
      this.collapsed = collapsedEventValue || !this.collapsed;
      if (this.expandable) {
        this.$emit('expandedIndex', this.currentIndex);
      }
      if (this.checklist.newFeature) {
        this.triggerExpandedEvent();
        this.clearNewFeatureAlert(this.checklist.feature);
      }
    },

    triggerExpandedEvent() {
      UiEvents.triggerButtonClicked({
        description: `The automatically populated ${this.checklist.headerText} feature was expanded`,
        page: this.$page,
        region: implementationChecklistRegion,
        section: `${this.checklist.headerText} Feature`,
        text: `${this.checklist.headerText}`,
      });
    },

    triggerSegmentEvent(event) {
      this.$emit('event', event);
    },
  },
};
</script>

<style module scoped>
.checklistContainer {
  background-color: var(--jcWhite50);
  border-radius: var(--jc-border-radius);
  box-shadow: var(--jc-box-shadow);
  margin-bottom: 1rem;
  min-width: 100%;
  overflow: inherit; /* workaround for legacy styles */
  padding: 1.5rem;
}

.extraPadding {
  padding: 2rem;
}

.checklistDisabled {
  pointer-events: none;
}

.feature {
  display: flex;
  font-size: 18px; /* workaround for legacy styles */
  width: 100%;
}

.featureTitle {
  align-items: center;
  display: flex;
}

.caret {
  align-self: center;
  margin-left: auto;
}

.card {
  margin-bottom: var(--jc-spacer-medium);
}

.cardContent {
  align-items: center;
  display: flex;
}

.checklistSectionContent {
  display: flex;
  padding-top: 0;
}

.checklistComplete {
  color: var(--jc-color-brand-500);
}

.checklistIncomplete {
  color: var(--jc-color-disabled-text);
}

.headerText {
  color: var(--jc-text-color-dark);
  white-space: initial;
}

.subText {
  margin-bottom: var(--jc-spacer-medium);
  margin-left: var(--jc-spacer-medium);
}

.infoIcon {
  color: var(--jcWarning900);
  padding-right: var(--jc-spacer-small);
}

.complete {
  text-decoration: line-through;
}

.taskContainer {
  display: inline-block;
  padding-left: 2.5rem;
}

.newFeature {
  background-color: var(--jc-color-success-base);
  border-radius: 50%;
  height: 0.75rem;
  margin-right: var(--jc-spacer-medium);
  width: 0.75rem;
}

.divider {
  background-color: var(--jc-color-neutral-stroke);
  border: none;
  height: 2px;
  width: 100%;
}

.removeBtn {
  margin-top: var(--jc-spacer);
}

.newFeatureSpacing {
  margin-top: var(--jc-spacer-small);
}

.newFeatureSpacingSmall {
  margin-top: var(--jc-spacer-x-small);
}
</style>
